import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Deal, DealStage } from '../../types';
import { deleteDeal, fetchDeals, updateDeal } from '../../services/api';
import { useSuperTenant } from '../../context/SuperAdminTenantContext';
import DealForm from './DealForm';
import { encodeId } from '../../utils/hashids';
import Pager from '../Pager';

const STAGES: DealStage[] = ['prospecting', 'negotiation', 'proposal', 'closed_won', 'closed_lost'];

const STAGE_LABELS: Record<DealStage, string> = {
  prospecting:   'OPEN',
  qualification: 'OPEN',
  proposal:      'PROPOSAL',
  negotiation:   'NEGOTIATION',
  closed_won:    'WON',
  closed_lost:   'LOST',
};

const STAGE_ACCENT: Record<DealStage, string> = {
  prospecting:   '#534AB7',
  qualification: '#534AB7',
  proposal:      '#185FA5',
  negotiation:   '#EF9F27',
  closed_won:    '#1D9E75',
  closed_lost:   '#D85A30',
};

const STAGE_BG: Record<DealStage, string> = {
  prospecting:   '#F2F1FF',
  qualification: '#F2F1FF',
  proposal:      '#DBEAFE',
  negotiation:   '#FFF8E1',
  closed_won:    '#E1F5EE',
  closed_lost:   '#FAECEA',
};

const initials = (name: string) =>
  name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const fmtInr = (n: number) =>
  n >= 100000
    ? `₹${(n / 100000).toFixed(1)}L/mo`
    : `₹${Number(n).toLocaleString('en-IN')}/mo`;

type ViewMode = 'kanban' | 'table';

const DealList: React.FC = () => {
  const history = useHistory();
  const { tenantId } = useSuperTenant();
  const [allDeals, setAllDeals]         = useState<Deal[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [search, setSearch]             = useState('');
  const [showForm, setShowForm]         = useState(false);
  const [editing, setEditing]           = useState<Deal | null>(null);
  const [defaultStage, setDefaultStage] = useState<DealStage>('prospecting');
  const [dragOverStage, setDragOverStage] = useState<DealStage | null>(null);
  const [view, setView]                 = useState<ViewMode>('kanban');
  const [page, setPage]                 = useState(1);
  const [lastPage, setLastPage]         = useState(1);
  const [total, setTotal]               = useState(0);
  const dragDeal = useRef<{ id: number; stage: DealStage } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetchDeals({ page, per_page: 50, tenant_id: tenantId || undefined });
      setAllDeals(res.data);
      setLastPage(res.last_page ?? 1);
      setTotal(res.total ?? res.data.length);
    } catch { setError('Failed to load deals'); }
    finally { setLoading(false); }
  }, [page, tenantId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [tenantId]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this deal?')) return;
    try { await deleteDeal(id); load(); }
    catch { alert('Failed to delete deal'); }
  };

  const handleSaved = () => { setShowForm(false); setEditing(null); load(); };

  const openEdit = (deal: Deal) => { setEditing(deal); setShowForm(true); };
  const openCreate = (stage: DealStage = 'prospecting') => {
    setEditing(null); setDefaultStage(stage); setShowForm(true);
  };

  const handleDragStart = (deal: Deal) => {
    dragDeal.current = { id: deal.id, stage: deal.stage };
  };

  const handleDrop = async (targetStage: DealStage) => {
    setDragOverStage(null);
    const src = dragDeal.current;
    if (!src || src.stage === targetStage) return;
    dragDeal.current = null;
    // optimistic update
    setAllDeals(prev => prev.map(d => d.id === src.id ? { ...d, stage: targetStage } : d));
    try {
      await updateDeal(src.id, { stage: targetStage });
    } catch {
      // revert on failure
      setAllDeals(prev => prev.map(d => d.id === src.id ? { ...d, stage: src.stage } : d));
    }
  };

  const filtered = search
    ? allDeals.filter(d =>
        d.title.toLowerCase().includes(search.toLowerCase()) ||
        (d.customer?.name ?? '').toLowerCase().includes(search.toLowerCase()) ||
        (d.organization?.name ?? '').toLowerCase().includes(search.toLowerCase())
      )
    : allDeals;

  // Map qualification → prospecting (display as OPEN)
  const byStage = STAGES.reduce<Record<DealStage, Deal[]>>((acc, s) => {
    if (s === 'prospecting') {
      acc[s] = filtered.filter(d => d.stage === 'prospecting' || d.stage === 'qualification');
    } else {
      acc[s] = filtered.filter(d => d.stage === s);
    }
    return acc;
  }, {} as Record<DealStage, Deal[]>);

  const pipelineValue = allDeals.filter(d => d.stage !== 'closed_lost').reduce((s, d) => s + Number(d.value), 0);
  const forecastWeighted = allDeals
    .filter(d => !['closed_won', 'closed_lost'].includes(d.stage))
    .reduce((s, d) => s + Number(d.value) * (d.probability ?? 50) / 100, 0);
  const wonThisMonth = allDeals.filter(d => d.stage === 'closed_won').reduce((s, d) => s + Number(d.value), 0);

  const fmtRaw = (n: number) => `₹${Number(n).toLocaleString('en-IN')}`;

  return (
    <div className="deals-page">
      {/* ── Header ── */}
      <div className="deals-header">
        <div className="deals-header-left">
          <h1 className="deals-title">Deals</h1>
          <div className="deals-pipeline-strip">
            <div className="pipeline-metric">
              <div className="pipeline-metric-label">Total Pipeline</div>
              <div className="pipeline-metric-value">{fmtRaw(pipelineValue)}</div>
            </div>
            <div className="pipeline-metric">
              <div className="pipeline-metric-label">Weighted Forecast</div>
              <div className="pipeline-metric-value" style={{ color: 'var(--primary)' }}>{fmtRaw(forecastWeighted)}</div>
            </div>
            <div className="pipeline-metric">
              <div className="pipeline-metric-label">Won This Month</div>
              <div className="pipeline-metric-value" style={{ color: 'var(--teal)' }}>
                ▲ {fmtRaw(wonThisMonth)}
              </div>
            </div>
          </div>
        </div>
        <div className="deals-header-right">
          <div className="deals-search-wrap">
            <svg className="deals-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input className="deals-search" placeholder="Search deals or organizations…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {/* View toggle */}
          <div className="deals-view-toggle">
            <button
              className={`deals-view-btn${view === 'kanban' ? ' deals-view-btn-active' : ''}`}
              title="Kanban view"
              onClick={() => setView('kanban')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="11" rx="1"/></svg>
            </button>
            <button
              className={`deals-view-btn${view === 'table' ? ' deals-view-btn-active' : ''}`}
              title="Table view"
              onClick={() => setView('table')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="1"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="9" x2="9" y2="21"/></svg>
            </button>
          </div>
          <button className="btn btn-primary" onClick={() => openCreate()}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            + New Deal
          </button>
        </div>
      </div>

      {loading && <div className="spinner">Loading pipeline…</div>}
      {error   && <div className="alert alert-error">{error}</div>}

      {!loading && !error && view === 'kanban' && (
        <div className="kanban-board">
          {STAGES.map(s => {
            const cards = byStage[s];
            const stageTotal = cards.reduce((sum, d) => sum + Number(d.value), 0);
            const accent = STAGE_ACCENT[s];
            const bg     = STAGE_BG[s];
            return (
              <div className="kanban-col" key={s}>
                <div className="kanban-col-header" style={{ borderTop: `3px solid ${accent}` }}>
                  <div className="kanban-col-header-top">
                    <span className="kanban-col-name" style={{ color: accent }}>{STAGE_LABELS[s]}</span>
                    <span className="kanban-col-count">{cards.length}</span>
                  </div>
                  {stageTotal > 0 && (
                    <div className="kanban-col-value">{fmtRaw(stageTotal)} total</div>
                  )}
                </div>

                <div
                  className={`kanban-cards${dragOverStage === s ? ' kanban-cards-over' : ''}`}
                  style={{ background: bg }}
                  onDragOver={e => { e.preventDefault(); setDragOverStage(s); }}
                  onDragLeave={() => setDragOverStage(null)}
                  onDrop={() => handleDrop(s)}
                >
                  {cards.map(deal => {
                    const orgName = deal.organization?.name ?? deal.customer?.name ?? '';
                    const assignee = deal.assigned_user;
                    return (
                      <div
                        className={`deal-card${s === 'closed_won' ? ' deal-card-won' : s === 'closed_lost' ? ' deal-card-lost' : ''}`}
                        key={deal.id}
                        draggable
                        onDragStart={() => handleDragStart(deal)}
                        onDragEnd={() => setDragOverStage(null)}
                      >
                        <div className="deal-card-accent" style={{ background: accent }} />
                        <div className="deal-card-body">
                          <div style={{ marginBottom: 4 }}>
                            <div className="deal-card-title">{deal.title}</div>
                            {orgName && <div className="deal-card-org">{orgName}</div>}
                          </div>

                          <div className="deal-card-row" style={{ marginBottom: 8 }}>
                            <span className="deal-card-value">{fmtInr(Number(deal.value))}</span>
                            {deal.probability != null && (
                              <span className="deal-card-prob">{deal.probability}%</span>
                            )}
                          </div>

                          <div className="deal-card-footer">
                            {deal.expected_close_date && (
                              <span className="deal-card-date">{deal.expected_close_date}</span>
                            )}
                            {assignee && (
                              <span className="avatar-xs" title={assignee.name}>{initials(assignee.name)}</span>
                            )}
                          </div>

                          <div className="deal-card-actions">
                            <button className="deal-action-btn deal-action-btn-view" onClick={() => history.push(`/deals/${encodeId(deal.id)}`)}>View</button>
                            <button className="deal-action-btn" onClick={() => openEdit(deal)}>Edit</button>
                            <button className="deal-action-btn deal-action-btn-danger" onClick={() => handleDelete(deal.id)}>Delete</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {cards.length === 0 && <div className="kanban-empty">No deals here</div>}
                </div>

                <button className="kanban-add-btn" onClick={() => openCreate(s)}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  + New Deal
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !error && view === 'table' && (
        <div className="deals-table-wrap">
          <table className="deals-table">
            <thead>
              <tr>
                <th>Deal Title</th>
                <th>Organization</th>
                <th>Stage</th>
                <th>Value</th>
                <th>Probability</th>
                <th>Close Date</th>
                <th>Assigned To</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="deals-table-empty">No deals found.</td></tr>
              )}
              {filtered.map(deal => {
                const accent  = STAGE_ACCENT[deal.stage] ?? '#888';
                const label   = STAGE_LABELS[deal.stage] ?? deal.stage;
                const orgName = deal.organization?.name ?? deal.customer?.name ?? '—';
                const assignee = deal.assigned_user;
                return (
                  <tr key={deal.id} className="deals-table-row" onClick={() => history.push(`/deals/${encodeId(deal.id)}`)}>
                    <td>
                      <div className="deals-tbl-title">{deal.title}</div>
                    </td>
                    <td className="deals-tbl-org">{orgName}</td>
                    <td>
                      <span className="deals-tbl-stage-pill" style={{ background: STAGE_BG[deal.stage], color: accent, borderColor: accent }}>
                        {label}
                      </span>
                    </td>
                    <td className="deals-tbl-value">{fmtInr(Number(deal.value))}</td>
                    <td>
                      {deal.probability != null ? (
                        <div className="deals-tbl-prob-wrap">
                          <div className="deals-tbl-prob-bar">
                            <div className="deals-tbl-prob-fill" style={{ width: `${deal.probability}%`, background: accent }} />
                          </div>
                          <span className="deals-tbl-prob-pct">{deal.probability}%</span>
                        </div>
                      ) : '—'}
                    </td>
                    <td className="deals-tbl-date">{deal.expected_close_date ?? '—'}</td>
                    <td>
                      {assignee
                        ? <span className="deals-tbl-assignee"><span className="avatar-xs">{initials(assignee.name)}</span>{assignee.name}</span>
                        : <em className="ld-empty">Unassigned</em>}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="deals-tbl-actions">
                        <button className="deal-action-btn deal-action-btn-view" onClick={() => history.push(`/deals/${encodeId(deal.id)}`)}>View</button>
                        <button className="deal-action-btn" onClick={() => openEdit(deal)}>Edit</button>
                        <button className="deal-action-btn deal-action-btn-danger" onClick={() => handleDelete(deal.id)}>Del</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && (
        <Pager currentPage={page} lastPage={lastPage} total={total} label="Deals" onChange={setPage} />
      )}

      {showForm && (
        <DealForm
          deal={editing}
          defaultStage={editing ? undefined : defaultStage}
          onSaved={handleSaved}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
};

export default DealList;
